// HomeScreen.tsx — "Today": the self-advancing recovery feed.
//
// The feed lists the steps still to do, current first. Opening an item and
// marking it complete (inside the viewer) makes its card fade + collapse out of
// the feed, which then glides to the next item — like a feed that scrolls
// itself onward. Each card can be favourited.
import React from 'react';
import { Capacitor } from '@capacitor/core';
import { Screen } from '@/components/Screen';
import { Display, Body, Meta, Pressable } from '@/components/ui';
import { Icon, BrandMark } from '@/components/icons';
import { FeedItemCard } from '@/components/FeedItemCard';
import { CollapsibleFade } from '@/components/CollapsibleFade';
import { InspireIntroCard } from '@/components/InspireIntroCard';
import { ProgrammeComplete } from '@/components/ProgrammeComplete';
import { NotificationTile } from '@/components/NotificationTile';
import { SignInTile } from '@/components/SignInTile';
import { useAuth } from '@/hooks/useAuth';
import { SHOW_PURCHASE, SHOW_IAP } from '@/config/supabase';
import { CheckinPromptTile } from '@/components/CheckinPromptTile';
import { canSync, openResearchCheckin, fetchMyProgress } from '@/services/sync';
import { researchCheckinDue, recordedWaveCount, lastWaveAt } from '@/services/researchCheckin';
import { fetchNotifications, getDismissed, dismissNotification, type AppNotification } from '@/services/notifications';
import { CalmYourBodyPanel } from '@/flows/calm/CalmYourBodyPanel';
import { NEW_UI } from '@/config/uiFlags';
import { SHOW_PROGRAMME_CTA, FOCUSED_ARRIVAL, TODAY_ONE_JOB } from '@/config/features';
import { ArrivalHeader } from '@/new/ArrivalHeader';
import { CareCard } from '@/new/CareCard';
import { ReminderNudgeTile, ReminderNudgeSheet } from '@/components/ReminderNudgeTile';
import { GuidePresence } from '@/flows/guide/GuidePresence';
import { useGuide } from '@/flows/guide/useGuide';
import { RETURN_AWAY_DAYS, ENCOURAGEMENT_EVERY_N_SESSIONS } from '@/data/guideMessages';
import type { GuideSelection } from '@/services/guidePresence';
import { CareTiles } from '@/flows/care/CareTiles';
import { JournalingChoiceTile } from '@/flows/journaling/JournalingChoiceTile';
import { AddToHomeScreenTile } from '@/components/AddToHomeScreenTile';
import { useProgress } from '@/hooks/useProgress';
import type { Programme, Step } from '@/types';
import { feedSteps, findStep, moduleOfStep, moduleName, nextStepAfter, allSteps, overallProgress, chapterLocator } from '@/services/contentProvider';
import { ModuleCelebration } from '@/flows/recovery/ModuleCelebration';
import { createPortal } from 'react-dom';
import { RouteToggle } from '@/components/RouteToggle';
import { dailyState, canOpen } from '@/services/compassionDaily';
import { wasAway, daysAway } from '@/services/presence';
import { dayKey, dayKeyOffset } from '@/services/dates';
import { chapterLoadFor } from '@/data/pacing.generated';
import { CompassionToday } from '@/flows/compassion/CompassionToday';

// Module-level latches (reset on a full reload, i.e. a new app launch). One session
// is counted per launch and the H11 note shows at most once per launch.
let sessionCountedThisLoad = false;
let encShownThisLoad = false;
// The arrival card opens EXPANDED on each fresh launch; once the user resolves it
// (uses a tool, taps Close, or starts on the programme) it stays collapsed for the
// rest of this launch — even if Today remounts on return from a viewer.
let arrivalResolvedThisLoad = false;
// Same idea for the programme feed: once the user reveals it (Continue / Open the
// programme / Rebuild), it stays revealed for the rest of this launch — even if Today
// remounts on return from a viewer (i.e. between modules). Without this latch feedOpen
// reset to false on every remount and the course feed vanished. Reset when the user
// changes their arrival (a fresh arrival re-hides the feed until they lead into it).
let feedRevealedThisLoad = false;
// First-launch teaching: the "Tap to start" cue on the live video tile can sit
// below the expanded arrival card, out of sight. We glide it into view ONCE per
// app load so a first-time user can't miss it. Latched (not per-mount) so it
// doesn't re-scroll every time Today remounts on return from a viewer.
let startHintScrolledThisLoad = false;
// When Today remounts because the user backed OUT of the Toolkit, land at the very top
// (the arrival header) rather than auto-scrolling to the revealed feed. A short time
// window (survives the mount + any StrictMode double-invoke) rather than a one-shot bool.
let landTodayAtTopUntil = 0;
/** Call right before closing the Toolkit so returning to Today lands at the top. */
export function landTodayAtTop(): void { landTodayAtTopUntil = Date.now() + 600; }

export function HomeScreen({
  programme,
  onStartBreathing,
  onSettleNow,
  onOpenCare,
  onOpenCrisis,
  onOpenStep,
  calmLocked = false,
  featureLock = false,
  onCalmLocked,
  onUnlock,
  onOpenJourney,
  onOpenProgress,
  onRollDice,
  onInspire,
  onOpenTool,
  onOpenLibraryTheme,
  onOpenToolkit,
  onOpenHold,
  showAsk = false,
  onOpenAsk,
  openCalmOnMount = false,
  onCalmOpened,
  openProgrammeOnMount = false,
  onProgrammeOpened,
  deferredActSurvey = null,
  onOpenActSurvey,
  pendingChapterFeedback = [],
  onOpenChapterFeedback,
  triage,
}: {
  programme: Programme;
  onStartBreathing: (protocolId: string, careEligible?: boolean) => void;
  /** NEW_UI arrival "Settle me" shortcut: run a fixed 2-minute panic-reset breathing
      session immediately (no picker), returning to the arrival screen on exit. */
  onSettleNow?: () => void;
  onOpenCare: () => void;
  onOpenCrisis: () => void;
  onOpenStep: (step: Step) => void;
  /** Accepted for API compatibility (callers still pass it); no longer used here
      now the reminder nudge opens its own sheet from the feed. */
  onOpenSettings?: () => void;
  /** True when Calm Your Body is member-gated and the user is not a member. */
  calmLocked?: boolean;
  /** Trial lapsed (non-member): premium features lock. Calm Your Body degrades to
   *  the panic route only; everything else offers a buy-to-unlock prompt. */
  featureLock?: boolean;
  onCalmLocked?: () => void;
  /** Show the small "Ask ReNurture" button (Beta) under the
   *  Today actions, and open the Ask overlay when tapped. */
  showAsk?: boolean;
  onOpenAsk?: () => void;
  /** Ask ReNurture "Calm" button: open the front-page Calm chooser when the host
   *  raises this flag, then call onCalmOpened so the host clears it (fires once). */
  openCalmOnMount?: boolean;
  onCalmOpened?: () => void;
  /** One-shot: land straight in the current lesson (Video 1 for a new user) right
   *  after onboarding. Reveals the feed first so completing it returns to the feed. */
  openProgrammeOnMount?: boolean;
  onProgrammeOpened?: () => void;
  /** Open the feature-unlock wall (from the "trial ended" notice). */
  onUnlock?: () => void;
  /** Open the Journey page (from the programme-complete close). */
  onOpenJourney?: () => void;
  onOpenProgress?: () => void;
  /** Roll the compassion dice (from the SC completion close). */
  onRollDice?: () => void;
  /** Open the "Inspire me" overlay (from the module-1 intro tile). */
  onInspire?: () => void;
  /** Open a toolkit exercise by id (Difficulty-with-ex tools). */
  onOpenTool: (toolId: string) => void;
  /** Open the Library filtered to a theme/category key (Difficulty-with-ex reads). */
  onOpenLibraryTheme: (theme: string) => void;
  /** NEW_UI header: open the full toolkit tab (Your stuff → Toolkit). */
  onOpenToolkit?: () => void;
  /** NEW_UI header: open the Hold page (boundary & urge tools). */
  onOpenHold?: () => void;
  /** TODAY_ONE_JOB: an act survey deferred out of the post-lesson chain (the act id),
   *  surfaced here as a "When you're ready" card. Null when nothing is pending. */
  deferredActSurvey?: string | null;
  onOpenActSurvey?: (actId: string) => void;
  /** TODAY_ONE_JOB: chapter-feedback queue deferred out of the post-lesson chain. */
  pendingChapterFeedback?: string[];
  onOpenChapterFeedback?: () => void;
  /** TRIAGE_V2 only: render the programme feed directly (no arrival header, feed forced open)
   *  with optional slots under the marquee and after the feed. Undefined = today's behaviour. */
  triage?: { underMarquee?: React.ReactNode; afterFeed?: React.ReactNode; header?: React.ReactNode };
}) {
  const progress = useProgress();
  const isCompassion = progress.state.activeProgramme === 'compassion';
  const auth = useAuth();
  const hr = new Date().getHours();
  const greet = hr < 12 ? 'morning' : hr < 18 ? 'afternoon' : 'evening';
  const dayName = new Date().toLocaleDateString('en-GB', { weekday: 'long' });

  // self-compassion daily cadence (today's exercise, week/day/days-left, lock)
  const daily = isCompassion ? dailyState(programme, progress.state, progress.state.devMode) : null;
  // Presence, not streaks — opening Today counts as showing up.
  const away = wasAway(progress.state.presenceDays);
  React.useEffect(() => { progress.recordPresence(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Guide presence on Today: H2 (return after away) only. The mastery (H5) and
  //    over-pacing (H7) guides now appear as beats INSIDE the programme flow
  //    (GuideFlowBeat) — not here — so they're seen while working, not on Today. ──
  const guide = useGuide();
  const todayKey = dayKey();
  const [h2] = React.useState<GuideSelection | null>(() =>
    daysAway(progress.state.presenceDays) >= RETURN_AWAY_DAYS
      ? guide.pick('H2_return', { alreadyHandledThisReturn: progress.state.guide.lastReturnHandled === todayKey })
      : null,
  );
  const [showH2, setShowH2] = React.useState(true);

  // Count one ordinary session per app launch (drives the H11 cadence).
  React.useEffect(() => {
    if (!sessionCountedThisLoad) { sessionCountedThisLoad = true; progress.bumpEncouragementSession(); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // "A word from Craig" (H11): a gentle, occasional founder note for just showing up.
  // It YIELDS to any weighted Today moment, fires at most once every N sessions, keeps
  // a 3-day floor (never twice within 3 days), and is suppressed while the programme is
  // paused (no nudging someone who has stepped back). Not on the very first session
  // (the N-session gap guarantees it). Ambient; tap to dismiss, then back next window
  // with a fresh quote.
  const enc = progress.state.encouragement;
  const weightedDue = !!h2 || !!progress.state.chapterCelebration || progress.state.guideMasteryDue || progress.state.guideOverPacingDue;
  const [h11, setH11] = React.useState<GuideSelection | null>(null);
  React.useEffect(() => {
    if (encShownThisLoad || weightedDue) return;
    if (progress.state.paused) return; // stepped back — no founder nudges
    if (enc.sessions - enc.lastFiredSession < ENCOURAGEMENT_EVERY_N_SESSIONS) return;
    // 3-day floor: skip if it last showed today, yesterday, or the day before.
    if (enc.lastDay && [0, 1, 2].some((d) => enc.lastDay === dayKeyOffset(d))) return;
    const sel = guide.pick('H11_encouragement');
    if (sel) { setH11(sel); encShownThisLoad = true; progress.markEncouragementShown(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enc.sessions, weightedDue]);

  // Scheduled wellbeing check-in (time-anchored from enrolment: baseline → 6 → 12 →
  // 24 weeks → every 8 weeks). This is the user's own Routine Outcome Monitoring, so
  // every signed-in user sees it (the check-in needs a token to mint their pid).
  // Research is a secondary, opt-in use — consent is passed through, not a gate.
  // The schedule itself lives in services/researchCheckin.
  // ONLY survivors are ever prompted for the check-in (the outcome measure); other
  // user types get the full app but never enter the research/outcome data.
  const canCheckin = canSync(auth.session) && progress.state.userType === 'survivor';
  // Backend-verified completion: refresh the recorded-wave count fail-soft on mount,
  // so a skipped/closed-browser check-in re-prompts (every REPROMPT_DAYS) until it is
  // actually recorded. Cached in progress state; offline simply keeps the last count.
  const checkinToken = canSync(auth.session) ? auth.session.accessToken : null;
  React.useEffect(() => {
    if (!checkinToken) return;
    let alive = true;
    const refresh = () => {
      fetchMyProgress(checkinToken)
        .then((ins) => {
          if (!alive || !ins) return;
          // Shared derivation (services/researchCheckin) so Home and Progress can
          // never write two different counts into the same cached field.
          const count = recordedWaveCount(ins);
          if (count == null) return;
          progress.setCompletedCheckinCount(count, lastWaveAt(ins));
        })
        .catch(() => {});
    };
    refresh();
    // The check-in itself opens OUTSIDE the app (a web form), so coming back is a
    // foreground event, not a remount — without this the count stayed stale and a
    // just-completed check-in kept its "Waiting on you" card until the app was
    // restarted. This is the other half of "the prompt stands until it is submitted":
    // the moment the server holds the submission, the card goes.
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', refresh);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', refresh);
    };
  }, [checkinToken]); // eslint-disable-line react-hooks/exhaustive-deps
  const completedCount = progress.state.completedCheckinCount;
  const checkin = canCheckin
    ? researchCheckinDue(progress.state, Date.now(), completedCount)
    // Dev-only: when not signed in (e.g. browser preview), force the baseline tile
    // so the layout is visible. Never ships — guarded by import.meta.env.DEV.
    : (import.meta.env.DEV
        ? { due: true, isBaseline: true, windowDay: 0, wave: 0 }
        : { due: false, isBaseline: false, windowDay: 0, wave: -1 });
  const startCheckin = () => {
    const s = auth.session;
    if (!canSync(s) || !s.accessToken) return;
    // Baseline (demographics) for anyone with no recorded wave yet — derived from the
    // backend count, not a local flag, so the first check-in always opens the
    // demographics version even after a half-finished earlier attempt.
    const firstCheckin = completedCount === 0;
    const consent = progress.state.researchConsent === 'granted';
    void openResearchCheckin(s.accessToken, undefined, { firstCheckin, consent });
    progress.markResearchCheckinOpened();
  };

  // True only when every step of the whole programme is done.
  const programmeComplete = overallProgress(programme, progress.state).pct >= 1;

  // Live notifications pushed from the backend (webinar invites, assessments…).
  // Fails soft: no backend / offline simply means no tiles.
  const [notifications, setNotifications] = React.useState<AppNotification[]>([]);
  React.useEffect(() => {
    let alive = true;
    fetchNotifications().then((all) => {
      if (!alive) return;
      const dismissed = getDismissed();
      setNotifications(all.filter((n) => !dismissed.includes(n.id)));
    });
    return () => {
      alive = false;
    };
  }, []);
  const onDismissNotification = (id: number) => {
    dismissNotification(id);
    setNotifications((cur) => cur.filter((n) => n.id !== id));
  };
  const target = feedSteps(programme, progress.state);
  const targetIds = target.map((s) => s.id);

  // `rows` is what's currently on screen; it can briefly hold a just-completed
  // id while it animates out, before settling to `targetIds`.
  const [rows, setRows] = React.useState<string[]>(targetIds);
  const [exitingId, setExitingId] = React.useState<string | null>(null);
  const firstRef = React.useRef<HTMLDivElement | null>(null);
  // NEW_UI scroll anchors: the Settle (Calm) panel and the programme/course selector.
  const calmRef = React.useRef<HTMLDivElement | null>(null);
  const programmeRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const removed = rows.filter((id) => !targetIds.includes(id));
    if (removed.length) {
      if (!exitingId) setExitingId(removed[0]);
      return;
    }
    const added = targetIds.filter((id) => !rows.includes(id));
    if (added.length || rows.length !== targetIds.length) {
      setRows(targetIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIds.join('|'), rows.join('|')]);

  const settle = React.useCallback(() => {
    setRows(targetIds);
    setExitingId(null);
    // glide to the new current card, just below the fixed header
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        firstRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIds.join('|')]);

  const currentId = targetIds[0];
  const curMod = currentId ? moduleOfStep(programme, currentId) : undefined;
  const orderedSteps = allSteps(programme);
  const curIndex = currentId ? orderedSteps.findIndex((s) => s.id === currentId) : -1;
  // First-video teaching hint: until the user has completed any video, show a
  // "Tap to start" cue on the featured video tile so the tap-to-play UI is obvious.
  const noVideoCompletedYet = !progress.state.completedSteps.some((sid) => findStep(programme, sid)?.type === 'video');
  // First launch only: if the "Tap to start" cue is live on the current video tile,
  // glide that tile into view (it otherwise renders below the expanded arrival card,
  // out of a new user's sight). Once per app load; block:'nearest' reveals the tile
  // while leaving the arrival selector in place above it.
  React.useEffect(() => {
    if (startHintScrolledThisLoad) return;
    // NEW_UI: Today leads with the arrival selector and the feed is dimmed until a
    // state is chosen — so never auto-scroll the feed into view (it was scrolling the
    // front screen down past the header on first load, e.g. straight out of onboarding).
    if (NEW_UI) return;
    const cueLive = !isCompassion && !!currentId && findStep(programme, currentId)?.type === 'video' && noVideoCompletedYet;
    if (!cueLive) return;
    startHintScrolledThisLoad = true;
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        firstRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // One-time intro tile: while the user is still in the very first module, point
  // them once to the header "Inspire me" button. It rides beneath the current
  // live tile and persists its dismissal. (Replaces the old in-feed testimonials,
  // which the same words are now reached through the reliable button instead.)
  const inFirstModule = !isCompassion && !!curMod && programme.modules[0]?.id === curMod.id;
  const showInspireIntro = inFirstModule && !progress.state.inspireIntroSeen;
  // The reminder nudge rides beneath the live tile to invite a gentle return
  // reminder, early in the journey. It used to be pinned to the SECOND chapter
  // only — but that chapter is just two steps, so the nudge flashed by and was
  // almost never seen. Instead, show it from chapter 2 ONWARD and keep it there
  // until the user actually sets a reminder or taps Hide, so the prompt can't be
  // missed. (Chapter 1 still belongs to the inspire intro, so the two never
  // collide.) Suppressed once a reminder exists or the tile was dismissed.
  const curModIndex = curMod ? programme.modules.findIndex((m) => m.id === curMod.id) : -1;
  const showReminderNudge =
    // Reminders are native-only (local notifications); on web they never fire, so
    // don't nudge web users to set one up.
    Capacitor.isNativePlatform() &&
    !isCompassion &&
    curModIndex >= 1 &&
    // Only nudge to set up notifications once the user has actually settled into the
    // app — at least 3 SEPARATE days of use — and only if they have none set.
    (progress.state.presenceDays?.length ?? 0) >= 3 &&
    !progress.state.morningAnchorTime &&
    !progress.state.reminderNudgeDismissed;

  // ── arrival card open/collapsed (owned here so a programme interaction can collapse
  //    it; resets to open on each fresh launch via the arrivalResolvedThisLoad latch). ──
  const [arrivalCollapsed, setArrivalCollapsed] = React.useState(arrivalResolvedThisLoad);
  // NEW_UI: the Calm Your Body emotions pathway, opened on demand from the new
  // header's "Settle the body" (renders below the header, expanded at 'emotions').
  const [calmOpen, setCalmOpen] = React.useState(false);
  // NEW_UI: whether a state has been chosen in the header. Until then, phase 1 is a
  // clean selector and the programme feed below is hidden.
  const [headerArrived, setHeaderArrived] = React.useState(false);
  // NEW_UI: the programme feed stays HIDDEN even after arriving, until the user taps
  // a button that leads into it (the header "Continue"/"Open the programme", or the
  // Rebuild menu tile). Keeps the landing on "how are you arriving" calm and roomy
  // instead of a wall of programme content. Reset on every arrival change below.
  const [feedOpen, setFeedOpen] = React.useState(feedRevealedThisLoad || !!triage);
  // The header "Set up a daily reminder" opens the notifications popup directly
  // (the time picker), not the full Settings page.
  const [remindersSheetOpen, setRemindersSheetOpen] = React.useState(false);
  // Always surface the Calm tile with its whole top in view. Runs after layout
  // settles (rAF) AND again on a short timeout (belt-and-braces for when the panel
  // content grows on a step change). scrollMarginTop clears the sticky header.
  const scrollIntoTop = React.useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const go = () => el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    requestAnimationFrame(() => requestAnimationFrame(go));
    setTimeout(go, 180); // after any layout shift (e.g. the Calm panel closing)
  }, []);
  const scrollCalmIntoView = React.useCallback(() => scrollIntoTop(calmRef.current), [scrollIntoTop]);
  // TODAY_ONE_JOB renders Calm as a full overlay, so there is nothing to scroll to.
  React.useEffect(() => { if (calmOpen && !TODAY_ONE_JOB) scrollCalmIntoView(); }, [calmOpen, scrollCalmIntoView]);
  // Reveal the (previously hidden) programme feed and scroll to its anchor. Called by
  // the header buttons that lead into the programme (Rebuild / Continue, and the
  // self-compassion route). The feed lives permanently in the DOM (display:none until
  // feedOpen), so the effect below fires ONLY on the false→true transition — a second
  // press leaves feedOpen already true, so the effect never re-runs and the old code
  // stopped scrolling after the first press. So scroll imperatively here on EVERY
  // press: scrollIntoTop is deferred (rAF ×2 + timeout), so on the first reveal it
  // lands after the display flips, and on repeat presses the feed is already open.
  const revealFeed = React.useCallback(() => {
    setCalmOpen(false);
    feedRevealedThisLoad = true;
    setFeedOpen(true);
    scrollIntoTop(programmeRef.current);
  }, [scrollIntoTop]);
  // Also scroll when the feed is already open on mount (returning from a lesson viewer
  // with the feed revealed earlier this load) — that path doesn't call revealFeed.
  React.useEffect(() => {
    if (Date.now() < landTodayAtTopUntil) return; // backed out of Toolkit → stay at the top
    if (feedOpen) scrollIntoTop(programmeRef.current);
  }, [feedOpen, scrollIntoTop]);
  const resolveArrival = React.useCallback(() => {
    arrivalResolvedThisLoad = true;
    setArrivalCollapsed(true);
  }, []);
  const expandArrival = React.useCallback(() => {
    arrivalResolvedThisLoad = false;
    setArrivalCollapsed(false);
  }, []);
  // Opening a programme step counts as "starting on the programme" — collapse the card.
  const openStep = React.useCallback((s: Step) => { resolveArrival(); onOpenStep(s); }, [resolveArrival, onOpenStep]);
  // The exact lesson "Continue your programme" will open: the current/next step in the
  // feed (progress-aware), falling back to the very first step. Shared by the button's
  // action AND its sub-label so they always agree.
  const continueStep = (currentId ? findStep(programme, currentId) : undefined) ?? orderedSteps[0];
  // Flatten a step's { pre, em, post } title to plain text for the button sub-label.
  const continueBlockedToday = isCompassion && !!continueStep && !canOpen(programme, continueStep, progress.state, !!progress.state.devMode);
  const continueSub = continueBlockedToday
    ? "Today's practice is done"
    : continueStep
    ? [ (continueStep.title as any)?.pre, (continueStep.title as any)?.em, (continueStep.title as any)?.post ]
        .filter(Boolean).join('').trim() || undefined
    : undefined;
  // "Continue with programme" arrival tile: put Today into feed/"rebuild" mode AND open
  // the last-visited programme (main OR self-compassion — `programme` is the active one)
  // at its next lesson. Same as tapping the lesson on the feed, so complete/next returns
  // to the revealed feed, not the arrival front page.
  const continueProgramme = React.useCallback(() => {
    revealFeed();
    if (continueStep && !continueBlockedToday) openStep(continueStep);
  }, [continueStep, continueBlockedToday, revealFeed, openStep]);
  // Ask ReNurture → "Calm" button: when the host raises this flag (Today is usually
  // already mounted under the Ask overlay, so this must react to the flag CHANGING,
  // not just to mount), open the emotions chooser — same as the header's "Settle the
  // body" — then clear the flag so it fires exactly once.
  React.useEffect(() => {
    if (openCalmOnMount) { resolveArrival(); setCalmOpen(true); onCalmOpened?.(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCalmOnMount]);
  // Land straight in the current lesson (Video 1 for a brand-new user) right after
  // onboarding. That first lesson STANDS ALONE: we deliberately call neither
  // revealFeed() nor openStep()'s resolveArrival(), so the feed stays hidden and the
  // arrival stays expanded behind it. Closing or completing the lesson therefore lands
  // on the arrival triage exactly as if the app had just been opened signed in —
  // which is also where the first check-in is waiting under "Waiting on you". (It used
  // to reveal the feed first and return there, which meant a new user never saw the
  // triage, so that check-in was never surfaced.) Clear the one-shot so it fires once.
  React.useEffect(() => {
    if (!openProgrammeOnMount || isCompassion) return;
    const s = (currentId ? findStep(programme, currentId) : undefined) ?? orderedSteps[0];
    if (s) onOpenStep(s);
    onProgrammeOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openProgrammeOnMount]);

  // The step just before the current one — offered as a small "back" so a user
  // who skipped the last lesson (e.g. an exercise) can return and repeat it.
  const prevStep = curIndex > 0 ? orderedSteps[curIndex - 1] : undefined;

  // Complete a step in place (image-only lessons advance from the tile itself).
  const completeStep = (s: Step) => {
    resolveArrival(); // completing in place also counts as engaging the programme
    const m = moduleOfStep(programme, s.id);
    if (!m) return;
    // Does finishing this step complete its chapter for the first time?
    const completesModule =
      !progress.isModuleComplete(m.id) && m.steps.every((x) => x.id === s.id || progress.isStepComplete(x.id));
    progress.markStepComplete(s.id, { moduleId: m.id, moduleStepIds: m.steps.map((x) => x.id) });
    if (/exercise/i.test(`${s.title.pre}${s.title.em || ''}`)) progress.addFavourite(s.id);
    const next = nextStepAfter(programme, s.id);
    if (next) progress.setCurrentStep(next.id);
    if (completesModule) {
      const load = chapterLoadFor(m.id);
      if (load === 'core' || load === 'skill') {
        const nextMod = next ? moduleOfStep(programme, next.id) : undefined;
        progress.setChapterCelebration({ moduleId: m.id, next: nextMod && nextMod.id !== m.id ? moduleName(nextMod) : '' });
      }
    }
  };

  // Chapter-complete celebration: a persisted signal set when a chapter completes
  // on a path that has no in-viewer celebration of its own (a chapter-ending
  // reflection that returns to Today, or an image-only step completed in place).
  // The small in-feed tile was retired — these now show the SAME full-screen
  // ModuleCelebration as in-viewer completions, since the full-screen version
  // lands better. Cleared on Continue.
  const justCompleted = progress.state.chapterCelebration;
  const dismissChapter = () => progress.setChapterCelebration(null);
  const celebratedModule = justCompleted ? programme.modules.find((m) => m.id === justCompleted.moduleId) ?? null : null;
  const chapterOverlay = justCompleted && celebratedModule ? (
    <ModuleCelebration
      module={celebratedModule}
      chapterLabel={(() => {
        const loc = chapterLocator(programme, celebratedModule.id);
        return loc.actName ? `${loc.actName} · Chapter ${loc.chapterNo}` : `Chapter ${loc.chapterNo}`;
      })()}
      nextModuleTitle={justCompleted.next || undefined}
      onContinue={dismissChapter}
    />
  ) : null;

  // ── programme feed tray ──────────────────────────────────────────────────
  // The programme feed sits on a warm gold tray so it reads as its own plane,
  // distinct from the cream header above. The accent is the active track's
  // colour. In landing states (caught up / course complete) we keep the body on
  // the cream — those components carry their own surfaces.
  const accentBright = isCompassion ? 'var(--feed-accent-green)' : 'var(--feed-accent-amber)';
  const feedOnBrown = isCompassion ? !!(daily && !daily.finished) : rows.length > 0;

  const feedBody =
    isCompassion && daily ? (
      daily.finished ? (
        <ProgrammeComplete
          onRegulate={() => onStartBreathing('relax_box')}
          onJourney={onOpenJourney}
          onRollDice={onRollDice}
          isCompassion
        />
      ) : (
        <>
          {/* "A word from Craig" (H11) — moved out of the header into the programme
              area so the header leads with the arrival selector. */}
          {h11 && !NEW_UI && (
            <div style={{ marginBottom: 12 }}>
              <GuidePresence selection={h11} onShown={() => guide.record(h11)} onClose={() => setH11(null)} />
            </div>
          )}
          <CompassionToday daily={daily} onOpenStep={openStep} />
        </>
      )
    ) : (
      <>
        {prevStep && (
          <Pressable
            as="button"
            onClick={() => openStep(prevStep)}
            ariaLabel="Go back to the previous step"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 12,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--ink-soft)',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              padding: '4px 2px',
              minHeight: 36,
            }}
          >
            <Icon name="chevLeft" size={15} /> Back to the previous step
          </Pressable>
        )}
        {rows.length === 0 ? (
          programmeComplete ? (
            <ProgrammeComplete
              onRegulate={() => onStartBreathing('relax_box')}
              onJourney={onOpenJourney}
              onOpenTool={onOpenTool}
              onOpenDashboard={onOpenProgress}
              onOpenPlan={() => {
                const plan = programme.modules.flatMap((m) => m.steps).find((s) => s.id === 'act3-c12-l06');
                if (plan) openStep(plan);
              }}
              onRollDice={onRollDice}
              isCompassion={isCompassion}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '30px 16px' }}>
              <BrandMark size={30} />
              <Display parts={{ pre: 'You’re all ', em: 'caught up', post: '.' }} size={20} style={{ marginTop: 14 }} />
              <Body dim size={13.5} style={{ marginTop: 8 }}>
                Nothing waiting today. Come back when you’re ready — the next chapter opens in time.
              </Body>
            </div>
          )
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {rows.map((id) => {
              const step = findStep(programme, id);
              if (!step) return null;
              const module = moduleOfStep(programme, id);
              if (!module) return null;
              const isExiting = id === exitingId;
              const featured = isExiting || id === currentId;
              const isNewCurrent = id === currentId && !isExiting;
              return (
                <React.Fragment key={id}>
                  <CollapsibleFade exiting={isExiting} onExited={settle}>
                    <FeedItemCard
                      step={step}
                      module={module}
                      featured={featured}
                      locked={!featured}
                      showStartHint={featured && step.type === 'video' && noVideoCompletedYet}
                      position={progress.getPosition(id)}
                      onOpen={openStep}
                      onComplete={completeStep}
                      cardRef={isNewCurrent ? firstRef : undefined}
                    />
                  </CollapsibleFade>
                  {/* Ambient tiles ride beneath the current live tile (the inspire-
                      tile pattern), so the header stays led by the arrival selector.
                      Chapter 1 → Inspire intro; chapter 2 onward → reminder nudge
                      (until a reminder is set or hidden); otherwise the occasional
                      "A word from Craig" (H11). At most one shows, so the feed
                      never re-clutters. */}
                  {isNewCurrent && (
                    <>
                      {showInspireIntro && (
                        <InspireIntroCard
                          onTry={() => {
                            progress.markInspireIntroSeen();
                            onInspire?.();
                          }}
                          onDismiss={() => progress.markInspireIntroSeen()}
                        />
                      )}
                      {showReminderNudge && <ReminderNudgeTile />}
                      {h11 && !NEW_UI && !showInspireIntro && !showReminderNudge && (
                        <GuidePresence selection={h11} onShown={() => guide.record(h11)} onClose={() => setH11(null)} />
                      )}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </>
    );

  return (
    <>
    {/* Full-screen chapter-complete celebration — portalled to <body> so it sits
        OUTSIDE the app's layout containers and truly covers the header + nav bar,
        instead of rendering inside the Today content area like a broken feed tile. */}
    {chapterOverlay && createPortal(chapterOverlay, document.body)}
    <Screen label="Today" surface="deep" center={NEW_UI && !feedOpen}>
      {triage ? (triage.header ?? null) : NEW_UI ? (
        <>
          {/* NEW header — the arrival-first top half; the real feed renders below. */}
          <ArrivalHeader
            onOpenCrisis={() => { setCalmOpen(false); onOpenCrisis(); }}
            onSettle={() => { resolveArrival(); setCalmOpen(true); if (!TODAY_ONE_JOB) scrollCalmIntoView(); }}
            // TODAY_ONE_JOB: the matched box's settle action — the arrival state already
            // answered "how are you feeling?", so go straight into the default 2-minute
            // settle (same free find_the_ground reset as "Settle me").
            onSettleDirect={() => { setCalmOpen(false); onSettleNow?.(); }}
            onRelax={() => { setCalmOpen(false); onStartBreathing('relax_box'); }}
            onSettleNow={() => { setCalmOpen(false); onSettleNow?.(); }}
            onHold={() => { setCalmOpen(false); onOpenHold?.(); }}
            onContinue={() => { revealFeed(); }}
            onContinueProgramme={continueProgramme}
            showAsk={showAsk}
            onOpenAsk={onOpenAsk}
            onSelfCompassion={() => { progress.setActiveProgramme('compassion'); revealFeed(); }}
            programmeCta={SHOW_PROGRAMME_CTA && !isCompassion ? {
              title: progress.state.completedSteps.length > 0 ? 'Continue your programme' : 'Start your programme',
              sub: curMod ? moduleName(curMod) : 'Begin with the first lesson',
              onOpen: () => {
                // Act EXACTLY like tapping the lesson on the programme feed: first put
                // Today into feed/"rebuild" mode (revealFeed sets the persistent
                // feedRevealedThisLoad latch), THEN open the lesson. That way next/
                // complete returns to the revealed feed — not the arrival front page.
                const s = (currentId ? findStep(programme, currentId) : undefined) ?? orderedSteps[0];
                revealFeed();
                if (s) openStep(s);
              },
            } : null}
            onOpenToolkit={() => { setCalmOpen(false); onOpenToolkit?.(); }}
            onOpenLibrary={() => { setCalmOpen(false); onOpenLibraryTheme(''); }}
            checkinDue={checkin.due}
            onStartCheckin={startCheckin}
            remindersSet={!!progress.state.morningAnchorTime}
            onSetupReminders={() => setRemindersSheetOpen(true)}
            // New users only: invite a first breathing session (relaxation time picker).
            // calmSessionsCount is bumped the moment a session is launched, so the card
            // disappears as soon as they've tried one.
            breathingNew={(progress.state.calmSessionsCount ?? 0) === 0}
            careDue={progress.state.careTriggered}
            onOpenCare={onOpenCare}
            onDismissCare={() => progress.dismissCare()}
            continueSub={continueSub}
            hasStarted={progress.state.completedSteps.length > 0}
            onArrivedChange={(a) => { setHeaderArrived(a); setCalmOpen(false); }}
            // Re-hide the feed ONLY when the user actively picks an arrival — not on the
            // mount-time onArrivedChange, which fires on every remount (e.g. a programme
            // switch) and used to clobber the feed + the feedRevealedThisLoad latch.
            onArrivalPicked={() => { setCalmOpen(false); feedRevealedThisLoad = false; setFeedOpen(false); }}
            // TODAY_ONE_JOB: post-lesson asks moved to the next arrival — they join the
            // "When you're ready" rotation here instead of interrupting a finished lesson.
            waitingExtras={TODAY_ONE_JOB ? [
              ...(deferredActSurvey && onOpenActSurvey ? [{
                key: 'actsurvey', icon: 'chart' as const,
                title: 'A short wellbeing check', sub: 'Six quick questions, whenever suits',
                onTap: () => onOpenActSurvey(deferredActSurvey),
              }] : []),
              ...(pendingChapterFeedback.length > 0 && onOpenChapterFeedback ? [{
                key: 'chapterfeedback', icon: 'pencil' as const,
                title: 'How was that chapter?', sub: 'A quick thought, only if you like',
                onTap: onOpenChapterFeedback,
              }] : []),
            ] : undefined}
          />
          {remindersSheetOpen && <ReminderNudgeSheet onClose={() => setRemindersSheetOpen(false)} />}
          {/* "Settle the body" → the emotions list → approach choice (Calm Your Body).
              TODAY_ONE_JOB: the chooser opens as a FULL OVERLAY (portalled over the app
              like the chapter celebration) instead of an inline insert below the waiting
              list — the user asked for Calm, so Calm is the whole screen. The panel's own
              back chevron (onCollapse) closes it. Web keeps the inline insert unchanged. */}
          {calmOpen && (TODAY_ONE_JOB ? (
            createPortal(
              <div
                role="dialog"
                aria-label="Calm your body"
                style={{
                  position: 'fixed', inset: 0, zIndex: 75, overflowY: 'auto',
                  background: 'var(--bg, #F7F5F2)',
                  padding: 'calc(var(--safe-top) + 14px) 16px calc(var(--safe-bottom) + 28px)',
                  animation: 'rise .32s cubic-bezier(.2,.7,.3,1)',
                }}
              >
                <div style={{ maxWidth: 560, margin: '0 auto' }}>
                  <CalmYourBodyPanel
                    key="settle"
                    initialStep="emotions"
                    newUi
                    onStartBreathing={onStartBreathing}
                    onOpenCrisis={onOpenCrisis}
                    onOpenTool={(id) => { setCalmOpen(false); onOpenTool(id); }}
                    onOpenLibraryTheme={(t) => { setCalmOpen(false); onOpenLibraryTheme(t); }}
                    onOpenCare={onOpenCare}
                    collapsed={false}
                    onCollapse={() => setCalmOpen(false)}
                    onExpand={() => {}}
                    locked={calmLocked}
                    featureLock={featureLock}
                    onLocked={onCalmLocked}
                  />
                </div>
              </div>,
              document.body,
            )
          ) : (
            <div ref={calmRef} style={{ marginTop: 14, scrollMarginTop: 'calc(var(--safe-top) + 60px)' }}>
              <CalmYourBodyPanel
                key="settle"
                initialStep="emotions"
                newUi
                onStepChange={scrollCalmIntoView}
                onStartBreathing={onStartBreathing}
                onOpenCrisis={onOpenCrisis}
                onOpenTool={onOpenTool}
                onOpenLibraryTheme={onOpenLibraryTheme}
                onOpenCare={onOpenCare}
                collapsed={false}
                onCollapse={() => setCalmOpen(false)}
                onExpand={() => {}}
                locked={calmLocked}
                featureLock={featureLock}
                onLocked={onCalmLocked}
              />
            </div>
          ))}
          {/* CARE prompt — surfaced AFTER settling (not as a persistent feed tile). In
              FOCUSED_ARRIVAL it appears in the header's "Waiting on you" list instead, so
              only render the standalone card in the legacy layout to avoid duplication. */}
          {!FOCUSED_ARRIVAL && headerArrived && progress.state.careTriggered && (
            <CareCard onOpen={onOpenCare} onDismiss={() => progress.dismissCare()} />
          )}
        </>
      ) : (
        <>
      {/* greeting */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
        <Display parts={away ? { pre: 'Welcome ', em: 'back', post: '.' } : { pre: 'Good ', em: greet, post: '.' }} size={26} />
        <Meta style={{ paddingBottom: 4, letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>{dayName}</Meta>
      </div>
      {/* a warm, pressure-free welcome — no streaks, no counts, no guilt */}
      <Body dim size={13} style={{ marginTop: 6 }}>
        Welcome back. However long it's been, this is still here for you.
      </Body>

      {/* H2 — guide welcome on return after time away (warm, never "where were you?") */}
      {h2 && showH2 && (
        <div style={{ marginTop: 16 }}>
          <GuidePresence selection={h2} onShown={() => guide.record(h2)} onClose={() => setShowH2(false)} />
        </div>
      )}

      {/* (H11 "A word from Craig" and the reminder nudge now ride in the programme
          feed beneath the live tile — not the header — so the top of Today leads with
          the arrival selector + check-ins. The old H6 check-in invite was removed: the
          expanded arrival selector below is the check-in, so the nudge was redundant.) */}

      {/* First-run: a compact "how would you like to journal?" choice. Hides itself
          once chosen; thereafter managed in Settings. */}
      <JournalingChoiceTile />

      {/* Calm Your Body — the arrival state selector. Opens EXPANDED at the top of
          every fresh launch (the user's state is the main route in); collapses to a
          quiet re-entry tile after a tool, Close, or starting on the programme. */}
      <div style={{ marginTop: 14 }}>
        <CalmYourBodyPanel
          onStartBreathing={onStartBreathing}
          onOpenCrisis={onOpenCrisis}
          onOpenTool={onOpenTool}
          onOpenLibraryTheme={onOpenLibraryTheme}
          onOpenCare={onOpenCare}
          collapsed={arrivalCollapsed}
          onCollapse={resolveArrival}
          onExpand={expandArrival}
          locked={calmLocked}
          featureLock={featureLock}
          onLocked={onCalmLocked}
        />
      </div>
        </>
      )}

      {/* Add-to-home-screen invite — web only. Self-hides in the native app, on
          desktop, when already launched from the home screen, and once dismissed. */}
      <AddToHomeScreenTile />

      {/* Trial-ended notice — once the 10-day trial lapses, the premium features are
          members-only; a gentle nudge to unlock (panic grounding + self-compassion
          stay free). Tapping opens the unlock wall (with the founder offer). */}
      {featureLock && onUnlock && (
        <Pressable
          as="button"
          onClick={onUnlock}
          ariaLabel="Your free trial has ended"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            textAlign: 'left',
            marginTop: 14,
            padding: '13px 15px',
            borderRadius: 16,
            border: '1px solid rgba(200,134,26,0.28)',
            background: 'rgba(200,134,26,0.08)',
            cursor: 'pointer',
          }}
        >
          <span aria-hidden="true" style={{ display: 'flex', flexShrink: 0, color: 'var(--amber-deep)' }}>
            <Icon name="lock" size={18} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, color: 'var(--ink)' }}>
              Your free trial has ended
            </span>
            <span style={{ display: 'block', marginTop: 2, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, color: 'var(--ink-soft)' }}>
              {SHOW_PURCHASE
                ? 'Meditations and tools are members-only now. Unlock the full programme.'
                : SHOW_IAP
                  ? 'Meditations and tools are part of membership. See the options.'
                  : 'Meditations and tools are part of membership, managed on the ReNurture website.'}
            </span>
          </span>
          <span style={{ color: 'var(--amber-deep)', flexShrink: 0, display: 'flex' }}>
            <Icon name="chevRight" size={16} />
          </span>
        </Pressable>
      )}

      {/* CARE — prompt + empowerment reminders after a Settle-My-Emotions session.
          Hidden in NEW_UI: the arriving screen stays a clean selector (this surfaces
          through the Calm flow instead, not as a persistent notification box). */}
      {!NEW_UI && <CareTiles onOpenCare={onOpenCare} />}

      {/* sign in (in-app onboarding) — only when activation is on & signed out */}
      {auth.enabled && auth.access === 'signedout' && (
        <div style={{ marginTop: 16 }}>
          <SignInTile />
        </div>
      )}

      {/* scheduled wellbeing check-in — time-anchored from enrolment (baseline →
          6 → 12 → 24 weeks → every 8 weeks). Optional and dismissible (snoozes
          this window). Shows for every signed-in user (their own outcome tracking). */}
      {!NEW_UI && checkin.due && (
        <div style={{ marginTop: 16 }}>
          <CheckinPromptTile
            isBaseline={checkin.isBaseline}
            onStart={startCheckin}
            onDismiss={() => progress.snoozeResearchCheckin()}
          />
        </div>
      )}

      {/* live notifications from the backend (invitations, assessments…) */}
      {!NEW_UI && notifications.length > 0 && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 11 }}>
          {notifications.map((n) => (
            <NotificationTile key={n.id} n={n} onDismiss={() => onDismissNotification(n.id)} />
          ))}
        </div>
      )}

      {/* the feed — laid on a warm dark-brown tray so the programme reads as its
          own plane, distinct from the lighter header above. White cards lift off
          it; the accent is the active track's colour (amber / green). */}
      <div
        ref={programmeRef}
        aria-hidden={NEW_UI && !feedOpen}
        style={{
          marginTop: 26,
          scrollMarginTop: 'calc(var(--safe-top) + 60px)',
          transition: 'opacity .3s ease',
          // Keep the programme feed hidden until the user chooses to enter it (taps
          // Continue / Open the programme / Rebuild). The arrival question and the
          // matched header stand alone first, so the landing stays calm and roomy;
          // the feed appears only on that deliberate tap.
          ...(NEW_UI && !feedOpen ? { display: 'none' as const } : {}),
        }}
      >
        {/* track switch sits on the cream, above the brown tray */}
        {!triage && <RouteToggle prominent={NEW_UI} style={{ marginBottom: 14 }} />}

        {/* programme marquee — eyebrow + chapter title, with an accent top edge */}
        <div
          style={{
            background: 'transparent',
            borderRadius: 24,
            padding: '4px 0 12px',
            boxShadow: 'none',
          }}
        >
          <div
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: accentBright,
              marginBottom: 5,
            }}
          >
            {isCompassion ? (daily && daily.weekNo ? `Self-Compassion · Week ${daily.weekNo}` : 'Self-Compassion · welcome') : 'ReNurture Programme'}
          </div>
          {isCompassion && daily ? (
            <>
              <Display
                parts={{ pre: daily.finished ? 'Course complete' : daily.dayLabel ? `${daily.dayLabel} · today` : 'Getting started', em: '' }}
                size={19}
                lh={1.15}
              />
              {!daily.finished && (
                <Meta style={{ display: 'block', marginTop: 4 }}>
                  {daily.weekNo ? `Week ${daily.weekNo} of 6` : 'A few introductions first'}
                </Meta>
              )}
            </>
          ) : (
            <Display
              parts={{ pre: curMod ? moduleName(curMod) : 'Continue your recovery', em: '' }}
              size={19}
              lh={1.15}
            />
          )}
        </div>

        {triage?.underMarquee}

        {/* feed body — continues the brown tray when there's an active feed,
            otherwise sits on the cream (caught-up / course-complete landings). */}
        {feedOnBrown ? (
          <div
            style={{
              background: 'transparent',
              borderRadius: 0,
              // Feed tiles run the full content width on the plain cream background.
              padding: '4px 0 14px',
              boxShadow: 'none',
            }}
          >
            {feedBody}
          </div>
        ) : (
          <div style={{ marginTop: 16 }}>{feedBody}</div>
        )}
        {triage?.afterFeed}
      </div>
    </Screen>
    </>
  );
}

